/**
 * `delay` is a function that can
 * be used to delay async tasks
 */
export const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

/**
 * `timeout` is a function that
 * can be used to time out promises
 */
export const timeout = <T>(promise: Promise<T>, ms: number) => {
  let timeoutId: NodeJS.Timeout

  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(Error('timeout'))
      }, ms)
    }),
  ]).finally(() => {
    clearTimeout(timeoutId)
  })
}

export interface RetryOptions {
  /**
   * Predicate to decide whether an error is retryable. When it
   * returns `false`, the retry loop aborts immediately and the
   * original error is re-thrown.
   */
  shouldRetry?: (error: unknown) => boolean
}

/**
 * `retry` invokes `callback` up to `retries` times (total attempts,
 * including the initial call), using exponential back-off between
 * attempts (1s, 2s, 4s, ... capped at 30s). An optional `shouldRetry`
 * predicate can be supplied to fast-fail on non-retryable errors.
 */
export async function retry<T>(
  callback: (retryCount?: number) => Promise<T>,
  retries: number,
  options: RetryOptions = {},
  retryCount = 0
): Promise<T> {
  try {
    return await callback(retryCount)
  } catch (error) {
    const isLastAttempt = retryCount >= retries - 1
    const canRetry = options.shouldRetry ? options.shouldRetry(error) : true
    if (isLastAttempt || !canRetry) {
      return Promise.reject<T>(error)
    }
    // back-off delay
    await delay(Math.min(1000 * 2 ** retryCount, 30000))
    return retry(callback, retries, options, retryCount + 1)
  }
}
